/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - JobRow (database schema - snake_case, JSONB metadata)
 * - Job   (UI schema - camelCase, flat fields)
 */

import type {
  Job,
  JobStage,
  MaterialReadiness,
  ProjectType,
} from "@/data/types";
import type { JobRow, JobMetadata } from "@/services/job.service";

const DEFAULT_JOB_TIMEZONE = "America/New_York";

function normalizeJobType(value: unknown, fallback: unknown): string {
  const raw = String(value ?? "").trim();
  if (raw) return raw;
  return String(fallback ?? "general").trim().toLowerCase() || "general";
}

function normalizeProjectTypeToJobType(value: unknown): string {
  const projectType = normalizeProjectType(value);
  switch (projectType) {
    case "Shower Remodel":
    case "Tub to Shower Conversion":
    case "Full Bathroom Remodel":
    case "Walk-in Tub Installation":
      return "bath";
    case "Window Install":
    case "Door Install":
      return "windows_doors";
    case "Flooring":
      return "flooring";
    case "Roofing":
      return "roofing";
    case "Painting":
      return "paint";
    case "Plumbing":
      return "plumbing";
    case "Drywall":
      return "drywall";
    default:
      return "";
  }
}

function normalizeProjectType(value: unknown, fallback?: unknown): ProjectType {
  const raw = String(value ?? fallback ?? "").trim();
  const text = raw.toLowerCase();
  if (text.includes("roof")) return "Roofing";
  if (text.includes("paint")) return "Painting";
  if (text.includes("plumb")) return "Plumbing";
  if (text.includes("drywall") || text.includes("sheetrock")) return "Drywall";
  if (text.includes("floor")) return "Flooring";
  if (text.includes("door")) return "Door Install";
  if (text.includes("window")) return "Window Install";
  if (text.includes("walk") && text.includes("tub")) return "Walk-in Tub Installation";
  if (text.includes("full") && text.includes("bath")) return "Full Bathroom Remodel";
  if (text.includes("tub") && text.includes("shower")) return "Tub to Shower Conversion";
  if (text.includes("shower")) return "Shower Remodel";
  return "Other";
}

function isUuid(val: string | undefined | null): val is string {
  if (!val) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
}

function normalizeMaterialReadiness(value: string | null | undefined): MaterialReadiness {
  const v = String(value ?? "").trim();
  switch (v) {
    case "Not Ordered":
    case "Ordered":
    case "Partially Received":
    case "Fully Received":
    case "Staged for Job":
    case "Loaded for Crew":
    case "Missing Items":
    case "Field Purchase Required":
      return v;
    // Legacy / alias values seen from warehouse/procurement pipelines
    case "Received":
      return "Fully Received";
    case "Staged":
      return "Staged for Job";
    case "Loaded":
      return "Loaded for Crew";
    default:
      return "Not Ordered";
  }
}

function pickCanonicalString(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const next = String(value ?? "").trim();
    if (next) return next;
  }
  return "";
}

/**
 * Canonical source-of-truth guard:
 * - Prefer top-level DB columns when present.
 * - Use metadata only as secondary fallback.
 */
function pickCanonicalField<T>(canonicalValue: T | null | undefined, metadataValue: T | null | undefined): T | undefined {
  if (canonicalValue !== null && canonicalValue !== undefined) return canonicalValue;
  if (metadataValue !== null && metadataValue !== undefined) return metadataValue;
  return undefined;
}

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiJob(db: JobRow): Job {
  const m = (db.metadata ?? {}) as JobMetadata;
  const sourceMeta = pickCanonicalField(
    db.source_meta ?? undefined,
    (m.sourceMeta ?? m.source_meta) as Job["sourceMeta"] | undefined,
  );
  const boundJobSpec = (m.boundJobSpec ?? m.bound_job_spec) as Job["boundJobSpec"] | undefined;
  const customerFromJoin = db.customers
    ? [db.customers.first_name, db.customers.last_name].filter(Boolean).join(" ").trim()
    : "";
  const customerName = pickCanonicalString(
    customerFromJoin,
    isUuid(db.customer_name) ? "" : db.customer_name,
    m.customerName,
  );
  const currentStage = pickCanonicalField(db.current_stage as JobStage | null | undefined, m.currentStage) ?? "Sale Closed";
  const scheduleStart = pickCanonicalField(
    db.scheduled_start_at ?? db.schedule_start_at,
    m.scheduledStartTime,
  );
  const scheduleEnd = pickCanonicalField(
    db.scheduled_end_at ?? db.schedule_end_at,
    m.scheduledEndTime,
  );
  const permitRequiredFromMetadata =
    typeof (m as { permitRequired?: unknown }).permitRequired === "boolean"
      ? ((m as { permitRequired?: boolean }).permitRequired as boolean)
      : undefined;
  const permitRequiredFromProjectConfig =
    typeof (m as { projectConfig?: { permitRequired?: unknown } }).projectConfig?.permitRequired === "boolean"
      ? ((m as { projectConfig?: { permitRequired?: boolean } }).projectConfig?.permitRequired as boolean)
      : undefined;
  const permitRequiredInferredFromStatus =
    (m.permitStatus && m.permitStatus !== "Not Required") ||
    !!m.permitDetail?.detailedStage;
  const permitRequired =
    db.permit_required ??
    permitRequiredFromMetadata ??
    permitRequiredFromProjectConfig ??
    permitRequiredInferredFromStatus;

  const payoutAmount = Number(
    m.payoutAmount ??
      m.financials?.subcontractorPayout ??
      0,
  );
  const offerCompensationConfig = m.offerCompensationConfig as Job["offerCompensationConfig"] | undefined;
  const assignedCrewRaw = String(m.assignedCrew ?? "").trim();
  const assignedSubRaw = String(m.assignedSubcontractor ?? "").trim();
  const assignmentTypeRaw = m.assignmentType ?? "Unassigned";
  const assignmentTypeNormalized: Job["assignmentType"] =
    assignmentTypeRaw === "Internal Crew" && assignedCrewRaw.length > 0
      ? "Internal Crew"
      : assignmentTypeRaw === "Subcontractor" && assignedSubRaw.length > 0
        ? "Subcontractor"
        : "Unassigned";
   const assignedSubcontractorId = (
     assignmentTypeNormalized === "Subcontractor"
       ? pickCanonicalString(
           String(m.assignedSubcontractorId ?? "").trim(),
           String(db.contractor_id ?? "").trim(),
         )
       : ""
   ).trim();
   // Determine the assigned subcontractor display name, falling back to contractor company name if needed
   let assignedSubcontractorDisplay = assignedSubRaw;
   if (!assignedSubcontractorDisplay && db.contractor_id) {
     // The contractors join is available via the contractors property on the JobRow (due to the select in the query)
     const contractorName = db.contractors?.company_name;
     if (contractorName) {
       assignedSubcontractorDisplay = contractorName;
     }
   }
   const crewId = pickCanonicalString(m.crewId, db.crew_id);

  return {
     // Prefer the DB primary id, fall back to job_number for legacy rows
    id: db.id ?? db.job_number,
    jobNumber: db.job_number ?? db.id,
    createdAt: db.created_at ?? new Date().toISOString().split("T")[0],
    updatedAt: db.updated_at ?? db.created_at ?? new Date().toISOString().split("T")[0],
    customerName,
    address: {
      street:
        [db.address_line1, db.address_line2].filter(Boolean).join(", ") ||
        [db.customers?.address_line1, db.customers?.address_line2].filter(Boolean).join(", ") ||
        "",
      city: db.city || db.customers?.city || "",
      state: db.state || db.customers?.state || "",
      zip: db.postal_code || db.customers?.postal_code || "",
    },
    phone: m.phone ?? "",
    email: m.email ?? "",
    secondaryContactName: m.secondaryContactName ?? "",
    secondaryContactPhone: m.secondaryContactPhone ?? "",
    secondaryContactEmail: m.secondaryContactEmail ?? "",
    organizationId: db.organization_id ?? undefined,
    sourceMeta: sourceMeta ?? undefined,
    projectType: normalizeProjectType(m.projectType, db.job_type),
    checklist: m.checklist as Job["checklist"],
    assignedCrew: assignedCrewRaw,
    crewId: crewId || undefined,
    assignmentType: assignmentTypeNormalized,
    scheduledInstallDate: scheduleStart
      ? scheduleStart.split("T")[0]
      : (m.scheduledStartTime?.split("T")[0] ?? m.targetInstallDate ?? ""),
    timeSlot: m.timeSlot,
    permitRequired,
    permitStatus: m.permitStatus ?? "Not Required",
    orderStatus: m.orderStatus ?? "Not Ordered",
    materialsStatus: m.materialsStatus ?? "Not Ready",
    dispatchStatus: m.dispatchStatus ?? "Not Ready",
    contractorAssignmentStatus: m.contractorAssignmentStatus ?? "",
    currentStage,
    notes: db.notes ?? "",
    issueFlag: m.issueFlag ?? false,
    scopeSummary: m.scopeSummary ?? "",
    description: m.description ?? "",
    payoutAmount: Number.isFinite(payoutAmount) ? payoutAmount : 0,
    offerCompensationConfig,
     assignedSubcontractorId: assignedSubcontractorId || undefined,
     assignedSubcontractor: assignedSubcontractorDisplay,
    targetInstallDate: m.targetInstallDate ?? "",
    activityHistory: m.activityHistory ?? [],
    photos: m.photos ?? [],
    photoGroups: m.photoGroups as Job["photoGroups"],
    qaSubmitted: m.qaSubmitted ?? false,
    materialReadiness: normalizeMaterialReadiness(db.material_readiness),
    materialReadinessNotes: m.materialReadinessNotes ?? "",
    missingItemsList: m.missingItemsList ?? "",
    procurementStatus: m.procurementStatus ?? "Not Ordered",
    warehouseStatus: m.warehouseStatus ?? "Not Received",
    branch: m.branch ?? "",
    region: m.region ?? "",
    salesRep: m.salesRep ?? "",
    permitDetail: m.permitDetail,
    orderDetail: m.orderDetail,
    closeoutStatus: m.closeoutStatus ?? "Not Started",
    finalDocumentationComplete: m.finalDocumentationComplete ?? false,
    customerSignoff: m.customerSignoff ?? false,
    closeoutNotes: m.closeoutNotes ?? "",
    closeoutSignoff: m.closeoutSignoff as Job["closeoutSignoff"],
    materialsList: m.materialsList,
    fieldMaterials: Array.isArray(m.fieldMaterials)
      ? (m.fieldMaterials as Job["fieldMaterials"])
      : undefined,
    unmappedSkusForReview: Array.isArray(m.unmappedSkusForReview)
      ? (m.unmappedSkusForReview as Job["unmappedSkusForReview"])
      : undefined,
    financials: m.financials,
    marketing: m.marketing,
    projectConfig: m.projectConfig as unknown as Job["projectConfig"],
    rescissionStatus: m.rescissionStatus ?? "Not Applicable",
    rescissionEndsAt: m.rescissionEndsAt,
    convertedFromQuoteId: m.convertedFromQuoteId,
    holdStatus: m.holdStatus,
    holdReason: m.holdReason,
    planningStatus: m.planningStatus,
    opportunityId: m.opportunityId,
    appointmentResultId: m.appointmentResultId,
    orderType: m.orderType,
    selectedVendor: m.selectedVendor,
    selectedVendorType: m.selectedVendorType,
    schedulingStatus: m.schedulingStatus,
    installReadiness: m.installReadiness,
    jobMaterialStatus: m.jobMaterialStatus,
    installStatus: m.installStatus,
    qaStatus: m.qaStatus,
    reviewSent: m.reviewSent ?? false,
    reviewCompleted: m.reviewCompleted ?? false,
    reviewSatisfied: m.reviewSatisfied ?? null,
    reviewSentAt: m.reviewSentAt,
    referralRequested: m.referralRequested ?? false,
    referralReceived: m.referralReceived ?? false,
    referralRequestedAt: m.referralRequestedAt,
    googleReviewUrl: m.googleReviewUrl,
    contractorRatingSent: m.contractorRatingSent ?? false,
    contractorRatingUrl: m.contractorRatingUrl,
    contractorRatingAt: m.contractorRatingAt,
    contractorRatingCompleted: m.contractorRatingCompleted ?? false,
    scopeLocked: m.scopeLocked ?? false,
    intakeApproved: m.intakeApproved ?? false,
    approvedForOrdering: m.approvedForOrdering ?? false,
    scheduledStartTime: scheduleStart ?? undefined,
    scheduledEndTime: scheduleEnd ?? undefined,
    scheduledShifts: typeof m.scheduledShifts === "number" ? m.scheduledShifts : undefined,
    timezone: m.timezone ?? DEFAULT_JOB_TIMEZONE,
    externalCalendarEventId: m.externalCalendarEventId,
    boundJobSpec,
    _portalChecklist: (m.checklist as unknown as Record<string, boolean> | undefined) ?? undefined,
    buildReviewChecklist: m.buildReviewChecklist as Job["buildReviewChecklist"],
    qaReviewerChecklist: m.qaReviewerChecklist as Job["qaReviewerChecklist"],
    changeOrders: m.changeOrders as Job["changeOrders"],
    contractDocument: m.contractDocument as Job["contractDocument"],
    migrationImportDetail: m.migrationImportDetail as Job["migrationImportDetail"],
    ntboStatus: db.ntbo_status,
    homeType: db.home_type,
    plumbingPermitStatus: db.plumbing_permit_status,
    parcelNumber: db.parcel_number,
    permitExpectationDate: db.permit_expectation_date,
    drawingFloorPlanCompleted: db.drawing_floor_plan_completed,
    secondBathroomDrawingCompleted: db.second_bathroom_drawing_completed,
    readyForInspection: db.ready_for_inspection,
    homeownerAvailableDate: db.homeowner_available_date,
    inspectionConfirmed: db.inspection_confirmed,
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────

export function toDbJobCreate(ui: Job): Omit<JobRow, "id" | "created_at" | "updated_at"> {
  const metadata: NonNullable<JobRow["metadata"]> = {
    customerName: ui.customerName,
    phone: ui.phone || undefined,
    email: ui.email || undefined,
    secondaryContactName: ui.secondaryContactName || undefined,
    secondaryContactPhone: ui.secondaryContactPhone || undefined,
    secondaryContactEmail: ui.secondaryContactEmail || undefined,
    projectType: ui.projectType,
    scopeSummary: ui.scopeSummary || undefined,
    description: ui.description || undefined,
    payoutAmount: ui.payoutAmount,
    offerCompensationConfig: ui.offerCompensationConfig,
    currentStage: ui.currentStage,
    permitStatus: ui.permitStatus,
    permitDetail: ui.permitDetail,
    orderStatus: ui.orderStatus,
    materialsStatus: ui.materialsStatus,
    dispatchStatus: ui.dispatchStatus,
    contractorAssignmentStatus: ui.contractorAssignmentStatus || undefined,
    assignedCrew: ui.assignedCrew || undefined,
    assignmentType: ui.assignmentType,
    assignedSubcontractor: ui.assignedSubcontractor || undefined,
    crewId: ui.crewId || undefined,
    materialReadinessNotes: ui.materialReadinessNotes || undefined,
    missingItemsList: ui.missingItemsList || undefined,
    procurementStatus: ui.procurementStatus,
    warehouseStatus: ui.warehouseStatus,
    orderDetail: ui.orderDetail,
    materialsList: ui.materialsList,
    fieldMaterials: ui.fieldMaterials,
    unmappedSkusForReview: ui.unmappedSkusForReview,
    closeoutStatus: ui.closeoutStatus,
    finalDocumentationComplete: ui.finalDocumentationComplete,
    customerSignoff: ui.customerSignoff,
    closeoutNotes: ui.closeoutNotes || undefined,
    closeoutSignoff: ui.closeoutSignoff,
    issueFlag: ui.issueFlag,
    qaSubmitted: ui.qaSubmitted,
    scopeLocked: ui.scopeLocked,
    intakeApproved: ui.intakeApproved,
    approvedForOrdering: ui.approvedForOrdering,
    financials: ui.financials,
    planningStatus: ui.planningStatus,
    opportunityId: ui.opportunityId,
    appointmentResultId: ui.appointmentResultId,
    orderType: ui.orderType,
    selectedVendor: ui.selectedVendor,
    selectedVendorType: ui.selectedVendorType,
    schedulingStatus: ui.schedulingStatus,
    installReadiness: ui.installReadiness,
    jobMaterialStatus: ui.jobMaterialStatus,
    installStatus: ui.installStatus,
    qaStatus: ui.qaStatus,
    rescissionStatus: ui.rescissionStatus,
    rescissionEndsAt: ui.rescissionEndsAt,
    convertedFromQuoteId: ui.convertedFromQuoteId,
    holdStatus: ui.holdStatus,
    holdReason: ui.holdReason,
    branch: ui.branch || undefined,
    region: ui.region || undefined,
    salesRep: ui.salesRep || undefined,
    activityHistory: ui.activityHistory,
    photos: ui.photos,
    photoGroups: ui.photoGroups,
    marketing: ui.marketing,
    projectConfig: ui.projectConfig as unknown as Record<string, unknown>,
    reviewSent: ui.reviewSent,
    reviewCompleted: ui.reviewCompleted,
    reviewSatisfied: ui.reviewSatisfied,
    reviewSentAt: ui.reviewSentAt,
    referralRequested: ui.referralRequested,
    referralReceived: ui.referralReceived,
    referralRequestedAt: ui.referralRequestedAt,
    googleReviewUrl: ui.googleReviewUrl,
    contractorRatingSent: ui.contractorRatingSent,
    contractorRatingUrl: ui.contractorRatingUrl,
    contractorRatingAt: ui.contractorRatingAt,
    contractorRatingCompleted: ui.contractorRatingCompleted,
    targetInstallDate: ui.targetInstallDate || ui.scheduledInstallDate || undefined,
    scheduledStartTime: ui.scheduledStartTime || undefined,
    scheduledEndTime: ui.scheduledEndTime || undefined,
    scheduledShifts: ui.scheduledShifts,
    timeSlot: ui.timeSlot,
    timezone: ui.timezone ?? DEFAULT_JOB_TIMEZONE,
    externalCalendarEventId: ui.externalCalendarEventId,
    buildReviewChecklist: ui.buildReviewChecklist,
    qaReviewerChecklist: ui.qaReviewerChecklist,
    boundJobSpec: ui.boundJobSpec,
    checklist: ui.checklist,
    changeOrders: ui.changeOrders,
    contractDocument: ui.contractDocument,
    migrationImportDetail: ui.migrationImportDetail,
  };

  return {
    job_number: ui.id,
    job_type: normalizeJobType(normalizeProjectTypeToJobType(ui.projectType), ui.checklist?.templateKey),
    crew_id: ui.crewId || null,
    scheduled_start_at: ui.scheduledStartTime || null,
    scheduled_end_at: ui.scheduledEndTime || null,
    address_line1: ui.address.street || null,
    address_line2: null,
    city: ui.address.city || null,
    state: ui.address.state || null,
    postal_code: ui.address.zip || null,
    material_readiness: ui.materialReadiness || null,
    permit_required: ui.permitRequired,
    notes: ui.notes || null,
    ntbo_status: ui.ntboStatus || null,
    home_type: ui.homeType || null,
    plumbing_permit_status: ui.plumbingPermitStatus || null,
    parcel_number: ui.parcelNumber || null,
    permit_expectation_date: ui.permitExpectationDate || null,
    drawing_floor_plan_completed: ui.drawingFloorPlanCompleted || null,
    second_bathroom_drawing_completed: ui.secondBathroomDrawingCompleted || null,
    ready_for_inspection: ui.readyForInspection || null,
    homeowner_available_date: ui.homeownerAvailableDate || null,
    inspection_confirmed: ui.inspectionConfirmed || null,
    metadata,
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────
// Only sends fields that are explicitly provided.

export function toDbJobUpdate(ui: Partial<Job>): Partial<Omit<JobRow, "id" | "created_at" | "updated_at">> {
  const patch: Partial<Omit<JobRow, "id" | "created_at" | "updated_at">> = {};

  if (ui.id !== undefined) patch.job_number = ui.id;
  if (ui.address !== undefined) {
    patch.address_line1 = ui.address.street || null;
    patch.city = ui.address.city || null;
    patch.state = ui.address.state || null;
    patch.postal_code = ui.address.zip || null;
  }
  if (ui.materialReadiness !== undefined) patch.material_readiness = ui.materialReadiness || null;
  if (ui.permitRequired !== undefined) patch.permit_required = ui.permitRequired;
  if (ui.notes !== undefined) patch.notes = ui.notes || null;
  if (ui.ntboStatus !== undefined) patch.ntbo_status = ui.ntboStatus || null;
  if (ui.homeType !== undefined) patch.home_type = ui.homeType || null;
  if (ui.plumbingPermitStatus !== undefined) patch.plumbing_permit_status = ui.plumbingPermitStatus || null;
  if (ui.parcelNumber !== undefined) patch.parcel_number = ui.parcelNumber || null;
  if (ui.permitExpectationDate !== undefined) patch.permit_expectation_date = ui.permitExpectationDate || null;
  if (ui.drawingFloorPlanCompleted !== undefined) patch.drawing_floor_plan_completed = ui.drawingFloorPlanCompleted || null;
  if (ui.secondBathroomDrawingCompleted !== undefined) patch.second_bathroom_drawing_completed = ui.secondBathroomDrawingCompleted || null;
  if (ui.readyForInspection !== undefined) patch.ready_for_inspection = ui.readyForInspection || null;
  if (ui.homeownerAvailableDate !== undefined) patch.homeowner_available_date = ui.homeownerAvailableDate || null;
  if (ui.inspectionConfirmed !== undefined) patch.inspection_confirmed = ui.inspectionConfirmed || null;

  // All business fields go into metadata — only set if any metadata fields changed
  const hasMetadataField = [
    "customerName", "phone", "email", "projectType", "currentStage",
    "permitStatus", "orderStatus", "materialsStatus", "dispatchStatus",
    "closeoutStatus", "issueFlag", "qaSubmitted", "scopeLocked", "buildReviewChecklist",
    "financials", "marketing", "activityHistory", "crewId", "boundJobSpec", "bound_job_spec",
    "offerCompensationConfig", "fieldMaterials",
    "changeOrders", "checklist", "unmappedSkusForReview", "migrationImportDetail",
  ].some((k) => k in ui);

  if (hasMetadataField) {
    patch.metadata = toDbJobCreate({ ...({} as Job), ...ui }).metadata;
  }

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiJobs(rows: JobRow[]): Job[] {
  return rows.map(toUiJob);
}
